import { useClub } from '@/features/settings/hooks/use-club'
import { usePreviewSession } from './use-preview-session'

export type PreviewShellGate = 'wait' | 'ready'

/**
 * ClubGate helper: when a preview session is active, decides whether the app
 * shell may render yet.
 *
 * Returns `'wait'` until the club doc has actually loaded under the preview
 * token — a successful read proves the token's `previewClubId` claim is live on
 * the Firestore client, so the dashboard's bookings / courts / activity-log
 * queries that mount next can't fire into the sign-in → claim auth-race window
 * (the intermittent "Missing or insufficient permissions" red banner).
 *
 * Returns `'ready'` once the club loads OR errors terminally (a genuine failure
 * shouldn't hang on the splash forever — let the shell render its own error),
 * and immediately when no preview is active (the gate falls through to its
 * normal membership logic).
 *
 * Lives here (preview-session/hooks) so the cross-feature `useClub` read is a
 * hooks-to-hooks crosscut — allowed by the boundary rules, unlike a component
 * reaching into `settings/hooks` directly. Mirrors `usePreviewClubName`.
 */
export function usePreviewClubReady(): PreviewShellGate {
  const session = usePreviewSession()
  const clubQuery = useClub(session.active ? session.clubId : null)
  if (!session.active || !session.clubId) return 'ready'
  if (clubQuery.data || clubQuery.isError) return 'ready'
  return 'wait'
}
